import {
  getActiveChecklistItemIndex,
  getActiveTourModuleIndex,
  getTourDataFromFirbase,
  tourChecklistToModuleMap,
  updateActiveChecklistItemIndex,
  updateActiveChecklistItemIndexDB,
  updateActiveTourModuleIndex,
  updateSpashScreenWelcomeModal,
  updateTourDataFromFirebase,
} from './slice';
import { getUserId } from '../auth/slice';
import { getWorkspaces } from '../workspace/slice';
import { updateAgentDataFromFirebase } from '../agent/slice';
import { dispatch, store } from '../root/store';
import { FirebaseTourProgress } from './model';
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  writeBatch,
  addDoc,
  query,
  limit,
} from 'firebase/firestore';
import { getActiveItemIndex } from './utilities';
import { getConfigValue } from '@zonofi/common';

const IS_PROD_ENV = getConfigValue('APP_ENV') === 'PROD';

// Tour JTBD Flow
/** Initializes the user's workspace details in users_<env>/<userId>/workspaces for a user in firebase */
export const initializeUserWorkspace = async (userId: string) => {
  const workspaces = getWorkspaces(store.getState());
  const userWorkspaceRef = collection(
    globalThis.sellerFirebasedb,
    IS_PROD_ENV ? 'users_prod' : 'users_qa',
    userId,
    'workspaces'
  );
  const workspaceSnapshot = await getDocs(query(userWorkspaceRef, limit(1)));
  if (!workspaceSnapshot.empty) {
    console.log(`Workspace already initialized for user ${userId}`);
    return []; // Or return existing checklist if needed
  }

  for (const workspace of workspaces) {
    try {
      await addDoc(userWorkspaceRef, workspace);
      console.log(
        `added workspace ${workspace} collection in users_${
          IS_PROD_ENV ? 'prod' : 'qa'
        }/${userId}/workspaces`
      );
    } catch (e) {
      throw new Error(
        `Failed to add workspace ${workspace} collection in users_${
          IS_PROD_ENV ? 'prod' : 'qa'
        }/${userId}/workspaces : ${e}`
      );
    }
  }
  return {
    success: true,
    message: `Added workspaces succefully for user, ${userId}`,
    userId,
  };
};

/** Initializes the user's checklist items in users_<env>/<userId>/checklist for a user in firebase */
export const initializeUserChecklist = async (userId: string) => {
  try {
    // Check if user's checklist already exists
    const userChecklistRef = collection(
      globalThis.sellerFirebasedb,
      IS_PROD_ENV ? 'users_prod' : 'users_qa',
      userId,
      'checklist'
    );

    const checklistSnapshot = await getDocs(query(userChecklistRef, limit(1)));

    if (!checklistSnapshot.empty) {
      console.log(`Checklist already initialized for user ${userId}`);
      dispatch(updateSpashScreenWelcomeModal(false));
      return []; // Or return existing checklist if needed
    }

    dispatch(updateSpashScreenWelcomeModal(true));
    // Get the checklist template
    const templateRef = collection(
      globalThis.sellerFirebasedb,
      IS_PROD_ENV ? 'checklist_template' : 'checklist_template_new'
    );
    const snapshot = await getDocs(templateRef);

    const initializedItems = [];
    for (const docSnap of snapshot.docs) {
      const templateItem = docSnap.data();
      const initialItem = {
        title: templateItem['title'],
        category: templateItem['category'],
        order: templateItem['order'],
        status: 'pending',
        createdAt: new Date().toISOString(),
        templateId: docSnap.id,
      };

      const docRef = await addDoc(userChecklistRef, initialItem);
      initializedItems.push({
        id: docRef.id,
        ...initialItem,
      });
    }

    console.log(`Initialized checklist for user ${userId}`);
    return initializedItems;
  } catch (error) {
    console.error(`Error initializing checklist for user: `, error);
    return [];
  }
};

/** fetches latest checklist data from firebase : "users_<env>/<userId>/checklist" and immedietely updates the redux store. Does not return any data. */
export const getTourProgress = async () => {
  console.log('Getting firebase tour data');
  const userId = getUserId(store.getState());

  console.log('userId from firebasecontroller :', userId);

  if (userId) {
    const checklistRef = collection(
      globalThis.sellerFirebasedb,
      IS_PROD_ENV ? 'users_prod' : 'users_qa',
      userId,
      'checklist'
    );
    console.log('collectionRef from firebasecontroller :', checklistRef);
    const snapshot = await getDocs(checklistRef);

    console.log('snapshot: ', snapshot);
    //@ts-ignore
    const dataFromFirebase: FirebaseTourProgress[] = snapshot.docs.map(
      (doc) => {
        const data = doc.data();
        return { id: doc.id, ...data };
      }
    );

    console.log('data from firebase : ', dataFromFirebase);

    // Sort data by order for processing
    const sortedData =
      dataFromFirebase?.sort((a, b) => a.order - b.order) || [];

    // Get current active step from store (for increment case)
    const currentActiveStep = getActiveChecklistItemIndex(store.getState());

    let activeStep = 1;

    // Determine if this is an increment call (when updateTourProgress calls getTourProgress)
    const isIncrementCall = currentActiveStep > 1;

    console.log(
      'isIncrementCall, currentActiveStep, nextStep : ',
      isIncrementCall,
      currentActiveStep,
      currentActiveStep + 1
    );

    if (isIncrementCall) {
      // Case 1: Increment current active item by 1
      const nextStep = currentActiveStep + 1;

      if (nextStep <= sortedData.length) {
        activeStep = nextStep;
      } else {
        // Case 2: If we're beyond the last item, find first pending item
        activeStep = findFirstPendingItem(sortedData);
      }
    } else {
      // Case 4: Initial data load - find first pending item
      activeStep = findFirstPendingItem(sortedData);
    }

    console.log('activeStep in getTourProgress : ', activeStep);

    dispatch(updateActiveChecklistItemIndexDB(activeStep));
    dispatch(updateActiveChecklistItemIndex(activeStep));
    dispatch(updateTourDataFromFirebase(dataFromFirebase));

    const getActiveContainerIndex = (checklistItemIndex: number) => {
      return checklistItemIndex === 0
        ? 0
        : tourChecklistToModuleMap.findIndex((i) =>
            i?.includes(checklistItemIndex)
          );
    };

    console.log(
      'setting active tour module index to :',
      getActiveContainerIndex(activeStep),
      ' from : ',
      getActiveTourModuleIndex(store.getState())
    );
    dispatch(updateActiveTourModuleIndex(getActiveContainerIndex(activeStep)));
  }

  return;
};

// Helper function to find the first pending item based on module priority
const findFirstPendingItem = (sortedData: FirebaseTourProgress[]) => {
  for (
    let moduleIndex = 0;
    moduleIndex < tourChecklistToModuleMap.length;
    moduleIndex++
  ) {
    const moduleItems = tourChecklistToModuleMap[moduleIndex];

    for (const itemOrder of moduleItems) {
      const item = sortedData.find((data) => data.order === itemOrder);

      if (item && item.status === 'pending') {
        return itemOrder;
      }
    }
  }

  // Case 3: If all items are completed, return to first item
  return 1;
};

/** Updates the checklist items's status from pending to completed in users_<env>/<userId>/checklist in firebase.userId>/checklist". If the item's status is already completed, it simply returns.
 * @param {string} itemId - The id of the checklist item to update
 */
export const updateTourProgress = async (itemId: string) => {
  const userId = getUserId(store.getState());
  const tourDataFromFirebase = getTourDataFromFirbase(store.getState());

  if (
    tourDataFromFirebase?.find((i) => i?.id === itemId)?.status !== 'completed'
  ) {
    const itemOrder = tourDataFromFirebase?.find(
      (i) => i?.id === itemId
    )?.order;
    if (itemOrder) {
      try {
        const itemRef = doc(
          globalThis.sellerFirebasedb,
          IS_PROD_ENV ? 'users_prod' : 'users_qa',
          userId,
          'checklist',
          itemId
        );
        await updateDoc(itemRef, {
          status: 'completed',
          updatedAt: new Date().toISOString(),
        });

        // Call getTourProgress to increment to next item
        await getTourProgress();
      } catch (e: any) {
        throw Error(`error while updating tour progress : ${e}`);
      }
    }
  } else {
    // Item already completed, just update the active index
    dispatch(
      updateActiveChecklistItemIndex(
        getActiveItemIndex(itemId, tourDataFromFirebase)
      )
    );
    return;
  }
};

/** Resets the checklist items's status from completed to pending in users_<env>/<userId>/checklist in firebase.userId>/checklist".
 * @param {string[]} itemIds - The list of checklist items' ids to be reset.
 */
export const resetTourProgress = async (itemIds: string[]) => {
  const userId = getUserId(store.getState());
  const batch = writeBatch(globalThis.sellerFirebasedb);

  const now = new Date().toISOString();

  itemIds.forEach((itemId) => {
    const itemRef = doc(
      globalThis.sellerFirebasedb,
      IS_PROD_ENV ? 'users_prod' : 'users_qa',
      userId,
      'checklist',
      itemId
    );
    batch.update(itemRef, {
      status: 'pending',
      updatedAt: now,
    });
  });

  await batch.commit();

  getTourProgress();
};

// Agent JTBD Flow
/** Initializes the agent flow for a user in firebase */
export const initializeAgentFlow = async (userId: string) => {
  try {
    const agentFlowItemsRef = collection(
      globalThis.sellerFirebasedb,
      IS_PROD_ENV ? 'users_prod' : 'users_qa',
      userId,
      'agent_flow'
    );

    const agentFlowItems = [
      { title: 'upload_knowledge', order: 1 },
      { title: 'test_agent', order: 2 },
      { title: 'waba_success', order: 3 },
    ];

    const agentFlowItemsSnapshot = await getDocs(
      query(agentFlowItemsRef, limit(1))
    );

    if (!agentFlowItemsSnapshot.empty) {
      console.log(`Agent flow already initialized for user ${userId}`);
      return []; // Or return existing agent flow if needed
    }

    const agentFlowUpdatedItems = [];
    for (const agentFlowItem of agentFlowItems) {
      const agentFlowItemDoc = await addDoc(agentFlowItemsRef, {
        title: agentFlowItem?.title,
        status: 'pending',
        order: agentFlowItem?.order,
        createdAt: new Date().toISOString(),
      });
      agentFlowUpdatedItems.push(agentFlowItemDoc);
      console.log(
        `Initialized agent flow item ${agentFlowItem} for user ${userId} : `,
        ' docId : ',
        agentFlowItemDoc?.id
      );
    }
    return agentFlowUpdatedItems;
  } catch (error) {
    throw new Error(`Error initializing agent flow for user: ${error}`);
  }
};

/** fetches latest agent flow data from firebase : "users_<env>/<userId>/agent_flow" and returns the data. */
export const getAgentFlowProgress = async () => {
  console.log('Getting firebase agent_flow data');
  const userId = getUserId(store.getState());

  console.log('userId from firebasecontroller :', userId);

  if (userId) {
    const agentFlowRef = collection(
      globalThis.sellerFirebasedb,
      IS_PROD_ENV ? 'users_prod' : 'users_qa',
      userId,
      'agent_flow'
    );
    console.log('agentFlowRef from firebasecontroller :', agentFlowRef);
    const snapshot = await getDocs(agentFlowRef);

    console.log('snapshot: ', snapshot);
    //@ts-ignore
    const agentFlowDataFromFirebase: AgentFlowProgress[] = snapshot.docs.map(
      (doc) => {
        const data = doc.data();

        return { id: doc.id, ...data };
      }
    );
    dispatch(updateAgentDataFromFirebase(agentFlowDataFromFirebase));
    return agentFlowDataFromFirebase;
  }

  return [];
};

/** Updates the agent flow items's status from pending to completed in users_<env>/<userId>/checklist in firebase.userId>/checklist". If the item's status is already completed, it simply returns.
 * @param {string} itemId - The id of the checklist item to update
 */
export const updateAgentFlowProgress = async (order: number) => {
  const userId = getUserId(store.getState());
  const agentFlowDataFromFirebase = await getAgentFlowProgress();
  if (
    agentFlowDataFromFirebase?.find((i) => i?.order === order)?.status !==
    'completed'
  ) {
    const itemId = agentFlowDataFromFirebase?.find(
      (i) => i?.order === order
    )?.id;
    console.log(
      'item id from updateAgentFlowProgress: ',
      itemId,
      'item status from updateAgentFlowProgress: ',
      agentFlowDataFromFirebase?.find((i) => i?.order === order)?.status
    );
    const itemRef = doc(
      globalThis.sellerFirebasedb,
      IS_PROD_ENV ? 'users_prod' : 'users_qa',
      userId,
      'agent_flow',
      itemId
    );
    await updateDoc(itemRef, {
      status: 'completed',
      updatedAt: new Date().toISOString(),
    });

    getAgentFlowProgress();
  }
};

export const updateTourItemsThroughNormalFlow = (
  isTourActive: boolean,
  tourDataFromFirebase: FirebaseTourProgress[],
  order: number
) => {
  if (!isTourActive && tourDataFromFirebase) {
    console.log('completing step 1 through normal flow');
    const reviewCustomerListItem = tourDataFromFirebase?.find(
      (item) => item?.order === order
    );
    if (
      reviewCustomerListItem &&
      reviewCustomerListItem?.status === 'pending'
    ) {
      console.log('found review customer list item : ', reviewCustomerListItem);
      updateTourProgress(reviewCustomerListItem?.id);
    } else {
      console.log(
        'no need to update review customer list already completed through normal flow'
      );
    }
  }
};

/** Resets the agent flow items's status from completed to pending in users_<env>/<userId>/agent_flow in firebase.userId>/agent_flow". */
export const resetAgentFlowProgress = async () => {
  const userId = getUserId(store.getState());
  const batch = writeBatch(globalThis.sellerFirebasedb);

  const now = new Date().toISOString();

  const agentFlowDataFromFirebase = await getAgentFlowProgress();
  const itemIds = agentFlowDataFromFirebase?.map((i) => i?.id);

  itemIds.forEach((itemId) => {
    const itemRef = doc(
      globalThis.sellerFirebasedb,
      IS_PROD_ENV ? 'users_prod' : 'users_qa',
      userId,
      'agent_flow',
      itemId
    );
    batch.update(itemRef, {
      status: 'pending',
      updatedAt: now,
    });
  });

  await batch.commit();
  getAgentFlowProgress();
};
